import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCreate, apiList, apiUpdate } from "@/lib/api";
import { toast } from "@/hooks/use-toast";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { clinicAddress, clinicValue } from "@/lib/clinic-information";

export interface InformacoesClinica {
  id: string;
  user_id?: string;
  cliente_id?: string;
  clinica_id?: string;
  nome_clinica: string;
  razao_social?: string;
  nome_fantasia?: string;
  cnpj?: string;
  cro_clinica?: string;
  telefone: string;
  celular?: string;
  email: string;
  website?: string;
  endereco: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade: string;
  estado: string;
  cep: string;
  logo_base64?: string;
  observacoes?: string;
  situacao_cadastral?: string;
  cnae_principal?: string;
  natureza_juridica?: string;
  porte?: string;
  data_abertura?: string;
  created_at: string;
  updated_at: string;
}

type ClinicaSaas = {
  id: string;
  cliente_id?: string;
  nome?: string;
  razao_social?: string;
  nome_fantasia?: string;
  cnpj?: string;
  cro_clinica?: string;
  telefone?: string;
  celular?: string;
  email?: string;
  website?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  situacao_cadastral?: string;
  cnae_principal?: string;
  natureza_juridica?: string;
  porte?: string;
  data_abertura?: string;
  logo_base64?: string;
};

export function mergeClinicInformation(detail: InformacoesClinica | null, clinic: ClinicaSaas | null): InformacoesClinica | null {
  if (!detail && !clinic) return null;
  if (detail && clinic?.id && detail.clinica_id && detail.clinica_id !== clinic.id) detail = null;
  const address = clinicAddress(clinic, detail);

  return {
    id: detail?.id || "",
    user_id: detail?.user_id,
    cliente_id: clinic?.cliente_id || detail?.cliente_id,
    clinica_id: clinic?.id || detail?.clinica_id,
    nome_clinica: clinicValue(clinic?.nome_fantasia || clinic?.nome, detail?.nome_clinica),
    razao_social: clinicValue(clinic?.razao_social, detail?.razao_social),
    nome_fantasia: clinicValue(clinic?.nome_fantasia || clinic?.nome, detail?.nome_fantasia || detail?.nome_clinica),
    cnpj: clinicValue(clinic?.cnpj, detail?.cnpj),
    cro_clinica: clinicValue(clinic?.cro_clinica, detail?.cro_clinica),
    telefone: clinicValue(clinic?.telefone, detail?.telefone),
    celular: clinicValue(clinic?.celular, detail?.celular),
    email: clinicValue(clinic?.email, detail?.email),
    website: clinicValue(clinic?.website, detail?.website),
    ...address,
    logo_base64: clinicValue(clinic?.logo_base64, detail?.logo_base64),
    observacoes: detail?.observacoes || "",
    situacao_cadastral: clinicValue(clinic?.situacao_cadastral, detail?.situacao_cadastral),
    cnae_principal: clinicValue(clinic?.cnae_principal, detail?.cnae_principal),
    natureza_juridica: clinicValue(clinic?.natureza_juridica, detail?.natureza_juridica),
    porte: clinicValue(clinic?.porte, detail?.porte),
    data_abertura: clinicValue(clinic?.data_abertura, detail?.data_abertura),
    created_at: detail?.created_at || "",
    updated_at: detail?.updated_at || "",
  };
}

export const useInformacoesClinica = () => {
  const queryClient = useQueryClient();
  const activeClinicaId = useActiveClinicaId();

  const { data: informacoes, isLoading } = useQuery({
    queryKey: ["informacoes-clinica", activeClinicaId],
    queryFn: async () => {
      const [rows, clinics] = await Promise.all([
        apiList<InformacoesClinica>("informacoes_clinica"),
        apiList<ClinicaSaas>("clinicas"),
      ]);
      const detail = rows
        .filter((row) => row.clinica_id === activeClinicaId)
        .sort((a, b) => String(b.updated_at || "").localeCompare(String(a.updated_at || "")))[0] || null;
      const clinic = clinics.find((row) => row.id === activeClinicaId) || null;
      return mergeClinicInformation(detail, clinic);
    },
    enabled: !!activeClinicaId,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const saveInformacoes = useMutation({
    mutationFn: async (dados: Omit<InformacoesClinica, "id" | "user_id" | "created_at" | "updated_at">) => {
      if (!activeClinicaId) throw new Error("Selecione uma clínica antes de salvar");
      const detailPayload = { ...dados, clinica_id: activeClinicaId };
      const clinicPayload = {
        nome_fantasia: dados.nome_fantasia || dados.nome_clinica,
        razao_social: dados.razao_social || "",
        cnpj: dados.cnpj || "",
        cro_clinica: dados.cro_clinica || "",
        telefone: dados.telefone || "",
        celular: dados.celular || "",
        email: dados.email || "",
        website: dados.website || "",
        endereco: dados.endereco || "",
        numero: dados.numero || "",
        complemento: dados.complemento || "",
        bairro: dados.bairro || "",
        cidade: dados.cidade || "",
        estado: dados.estado || "",
        cep: dados.cep || "",
        situacao_cadastral: dados.situacao_cadastral || "",
        cnae_principal: dados.cnae_principal || "",
        natureza_juridica: dados.natureza_juridica || "",
        porte: dados.porte || "",
        data_abertura: dados.data_abertura || "",
        logo_base64: dados.logo_base64 || "",
      };

      await apiUpdate<ClinicaSaas>("clinicas", activeClinicaId, clinicPayload);
      if (informacoes?.id) {
        return apiUpdate<InformacoesClinica>("informacoes_clinica", informacoes.id, detailPayload);
      }
      return apiCreate<InformacoesClinica>("informacoes_clinica", detailPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["informacoes-clinica"] });
      queryClient.invalidateQueries({ queryKey: ["clinicas-oficiais"] });
      toast({ title: "Informacoes salvas", description: "As informacoes da clinica foram salvas com sucesso." });
    },
    onError: (error) => {
      toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" });
    },
  });

  return { informacoes, isLoading, saveInformacoes: saveInformacoes.mutate, isSaving: saveInformacoes.isPending };
};
